import React, { useRef, useState } from 'react';
import LoginProvider, {
  formDefaultValues,
  useLoginContext,
  FORM_IDS,
} from 'providers/LoginProvider';
import { hasValue } from 'swipeclock-frontend';
import { useLoginInputHandler } from 'hooks/useLoginInputHandler';
import { styled } from '@mui/material/styles';
import { useMediaQuery, useTheme } from '@mui/material';
import { usePostForgotPasswordMutation } from 'services/login';
import { useBuildLoginURL, useUniversaLoginUser } from 'hooks/useLoginUser';
import { ErrorTransitionWrapper } from 'components/uncontrolled/Styles';
import { CSSTransition } from 'react-transition-group';
import { setPublicPageTitle } from 'helpers/documentHelper';
import Alert from '@mui/material/Alert';
import ButtonSet from 'components/ModalView/DefaultButtonSet';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Logo from 'assets/WFHLogoAndText.png';
import PasswordTextfield from 'components/uncontrolled/PasswordTextfield';
import PropTypes from 'prop-types';
import Textfield from 'components/uncontrolled/Textfield';

export default function LoginApp() {
  setPublicPageTitle('Login');
  return (
    <LoginProvider defaultValues={formDefaultValues}>
      <LoginWrapper />
    </LoginProvider>
  );
}

function LoginWrapper() {
  const { state, meta, actions } = useLoginContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const errorRef = useRef();
  const [localMessage, setLocalMessage] = useState();
  const [postForgotPassword] = usePostForgotPasswordMutation();

  const startCurrent = state.currentPage === meta?.pages?.START;

  const loginBag = useLoginInputHandler({
    ids: [FORM_IDS.USERNAME, FORM_IDS.PASSWORD],
    validationCallback,
  });
  const {
    getError,
    handleBlur,
    handleChange,
    setDirty,
    isValid,
    validationRef,
  } = loginBag;

  const handlePasswordReset = async () => {
    const valid = isValid(meta?.pages?.FORGOTPASS);
    if (valid) {
      const values = Object.keys(validationRef.current).reduce((acc, key) => {
        if (key === 'username') {
          acc.email = validationRef.current[key]?.value;
        }
        return acc;
      }, {});

      // post password reset mutation
      const { error } = await postForgotPassword({ body: values });

      if (error) {
        const message =
          "We're sorry, there was an error. Please try again or contact your company administrator for assistance.";
        actions.setGlobalMessage({ message, severity: 'error' });
      } else {
        const message =
          'Success! If the email entered is associated with a WorkforceHub account, a reset email should appear in your inbox momentarily.';
        actions.setGlobalMessage({ message, severity: 'success' });
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handlePasswordReset();
      setDirty((prevDirty) => ({
        ...prevDirty,
        [FORM_IDS.USERNAME]: true,
      }));
    }
  };

  return (
    <FormWrapper>
      <FormInputGrid raised={!isMobile}>
        <HeaderImage src={Logo} alt="Workforce Hub Logo" />
        <CSSTransition
          in={!!state.globalMessage}
          timeout={theme.transitions.duration.shorter}
          classNames="error-text"
          nodeRef={errorRef}
          // localMessage is set below onEnter (prior to beginning the transition)
          // and removed onExited (after the transition has finished).
          // Otherwise the abrupt change of state.globalMessage to a falsy state
          // prevents the exit transition from rendering on screen
          onEnter={() => setLocalMessage(state.globalMessage?.message)}
          onExited={() => setLocalMessage()}
        >
          <ErrorTransitionWrapper ref={errorRef}>
            {localMessage && (
              <ErrorField severity={state.globalMessage?.severity}>
                {localMessage}
              </ErrorField>
            )}
          </ErrorTransitionWrapper>
        </CSSTransition>
        <EmailField
          autoComplete="email"
          error={getError(FORM_IDS.USERNAME)}
          id={FORM_IDS.USERNAME}
          label="Email"
          maxLength={50}
          onBlur={handleBlur(FORM_IDS.USERNAME)}
          onChange={handleChange(FORM_IDS.USERNAME)}
          placeholder="Email Address"
          defaultValue={formDefaultValues.email}
          required
          type="email"
          validationRef={validationRef}
          inputProps={!startCurrent ? { onKeyDown: handleKeyDown } : {}}
        />
        {startCurrent ? (
          <Login loginBag={loginBag} />
        ) : (
          <ForgotPass
            loginBag={loginBag}
            handlePasswordReset={handlePasswordReset}
          />
        )}
        <FooterLink href="https://www.swipeclock.com/">
          Powered by Swipeclock
        </FooterLink>
      </FormInputGrid>
    </FormWrapper>
  );
}

function Login({ loginBag }) {
  const {
    checkFormValidity,
    currentPageRef,
    disabled,
    getError,
    handleBlur,
    handleChange,
    isValid,
    setDirty,
    validationRef,
  } = loginBag;

  const {
    actions,
    meta: { pages },
  } = useLoginContext();
  const buildLoginUrl = useBuildLoginURL();
  const loginUser = useUniversaLoginUser();
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async () => {
    const valid = isValid(pages.START);
    if (!valid) {
      // the login button is disabled unless the form is valid so checking isValid is redundant.
      // Adding isValid here as an additional check just in case it is needed later for some reason.
      throw new Error('Form is invalid.');
    }

    try {
      setSubmitting(true);
      const values = extractValues(validationRef);
      const { hasWFHEnabled, token } = await loginUser(values);
      const loginUrl = buildLoginUrl({ hasWFHEnabled, token });

      // check if window is available and if so redirect to the login url
      if (typeof window !== 'undefined') {
        window.location.replace(loginUrl);
      }
    } catch (error) {
      setSubmitting(false);
      console.error(error);
      const errorText =
        "We're sorry, the email address or password you entered is not recognized.";
      // Display generic error message
      actions.setGlobalMessage({ message: errorText, severity: 'error' });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleLogin();
      setDirty((prevDirty) => ({
        ...prevDirty,
        [FORM_IDS.PASSWORD]: true,
      }));
    }
  };

  return (
    <>
      <PasswordField
        autoComplete="password"
        error={getError(FORM_IDS.PASSWORD)}
        id={FORM_IDS.PASSWORD}
        label="Password"
        maxLength={50}
        onBlur={handleBlur(FORM_IDS.PASSWORD)}
        onChange={handleChange(FORM_IDS.PASSWORD)}
        defaultValue={formDefaultValues.password}
        placeholder="Access Code/Password"
        required
        validationRef={validationRef}
        inputProps={{ onKeyDown: handleKeyDown }}
      />
      <ButtonWrapper>
        <ButtonSet
          orientation="vertical"
          primary={{
            name: 'Login',
            action: handleLogin,
            disabled: disabled || !isValid(pages.START),
            isBusy: submitting,
          }}
          secondary={{
            name: 'Forgot Password?',
            action: (ev) => {
              currentPageRef.current = pages.FORGOTPASS;
              actions.setCurrentPage(pages.FORGOTPASS);
              checkFormValidity(ev);
            },
          }}
        />
      </ButtonWrapper>
    </>
  );
}

Login.propTypes = { loginBag: PropTypes.object };

function ForgotPass({ loginBag, handlePasswordReset }) {
  const {
    actions,
    meta: { pages },
  } = useLoginContext();
  const [, { isLoading }] = usePostForgotPasswordMutation();

  const { checkFormValidity, currentPageRef, disabled, isValid } = loginBag;

  return (
    <>
      <StyledText>
        We'll send a reset email to the address entered above. If you don't
        receive an email or have additional problems logging in, please contact
        your company administrator.
      </StyledText>
      <ButtonWrapper>
        <ButtonSet
          orientation="vertical"
          primary={{
            name: 'Submit',
            action: handlePasswordReset,
            disabled: disabled || !isValid(pages.FORGOTPASS) || isLoading,
          }}
          secondary={{
            name: 'Return to Login',
            action: (ev) => {
              currentPageRef.current = pages.START;
              actions.setCurrentPage(pages.START);
              checkFormValidity(ev);
            },
          }}
        />
      </ButtonWrapper>
    </>
  );
}

ForgotPass.propTypes = {
  loginBag: PropTypes.object,
  handlePasswordReset: PropTypes.func,
};

// ########################################################
// #### Styled Components
// ########################################################

const FormWrapper = styled(Box)(({ theme }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'baseline',
    width: '100%',
    height: '100dvh',
    paddingTop: isMobile
      ? 'inherit'
      : `max(${theme.spacing(4)}, calc(100dvh - 525px - 40dvh))`,
    backgroundColor: isMobile
      ? theme.palette.primary.contrastText
      : theme.palette.primary.main,
  };
});

const FormInputGrid = styled((props) => <Card {...props} />)(({ theme }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return {
    display: 'grid',
    gridTemplateAreas: `
    'header'
    'error'
    'email'
    'password'
    'buttons'
    'footer'
    `,
    gridTemplateColumns: '1fr',
    gap: theme.spacing(4),
    columnGap: theme.spacing(4),
    padding: theme.spacing(4),
    width: isMobile ? '100%' : '75%',
    maxWidth: '600px',
    backgroundColor: theme.palette.primary.contrastText,
    boxShadow: `0px 5px 5px -3px rgba(0,0,0,${
      isMobile ? 0.0 : 0.2
    }), 0px 8px 10px 1px rgba(0,0,0,${
      isMobile ? 0.0 : 0.14
    }), 0px 3px 14px 2px rgba(0,0,0,${isMobile ? 0.0 : 0.12})`,
  };
});

const EmailField = styled(Textfield)(() => ({
  gridArea: 'email',
}));

const ErrorField = styled((props) => <Alert {...props} />)(() => ({
  gridArea: 'error',
}));

const PasswordField = styled(PasswordTextfield)(() => ({
  gridArea: 'password',
}));

const HeaderImage = styled('img')(() => ({
  width: '100%',
  gridArea: 'header',
}));

const ButtonWrapper = styled(Box)(({ theme }) => ({
  gridArea: 'buttons',
  marginTop: theme.spacing(3),
}));

const FooterLink = styled('a')(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.primary.main,
  fontSize: theme.spacing(1.5),
  textDecoration: 'none',
  gridArea: 'footer',
}));

const StyledText = styled('div')(({ theme }) => ({
  textAlign: 'left',
  width: '100%',
  fontSize: theme.spacing(1.75),
}));

// ########################################################
// #### Helper Functions
// ########################################################

function extractValues(validationRef) {
  return Object.keys(validationRef.current).reduce((acc, key) => {
    acc[key] = validationRef.current[key]?.value;
    return acc;
  }, {});
}

const validationCallback = (id, value, currentPage, pages) => {
  let error = null;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isPasswordResetPage =
    hasValue(currentPage?.current) &&
    hasValue(pages?.FORGOTPASS) &&
    currentPage?.current === pages?.FORGOTPASS;
  switch (id) {
    case FORM_IDS.USERNAME:
      if (!hasValue(value)) {
        error = 'Email is required.';
      } else if (isPasswordResetPage && !emailRegex.test(value)) {
        error = 'Email must be a valid email address.';
      }
      break;
    case FORM_IDS.PASSWORD: {
      if (!hasValue(value)) {
        error = 'Password is required.';
      }
      break;
    }
    default:
      break;
  }
  return error;
};
